import { useMemo, useState } from "react";
import { Loader2, Receipt, Layers, Files, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { rupiah, fmtDate } from "@/lib/format";
import { termDue } from "@/components/finance/termDue";
import { todayStr } from "@/components/finance/BillDialog";
import { usePaymentTerms, findTerm, TermSelect, TermHint } from "@/components/finance/TermSelect";

function groupByCustomer(rows) {
  const m = new Map();
  rows.forEach((r) => {
    const k = r.customer_odoo_id;
    if (!m.has(k)) m.set(k, { cid: k, outlet: r.outlet, rows: [], head: r });
    m.get(k).rows.push(r);
  });
  return [...m.values()];
}

function GroupCard({ g, mode, terms, termId, setTerm, invDate }) {
  const total = g.rows.reduce((s, r) => s + r.estimate.amount, 0);
  const t = findTerm(terms, termId);
  const due = t ? termDue(invDate, t.lines, t.days || 0) : termDue(invDate, g.head.ar_term_lines, g.head.ar_term_days || 0);
  const merged = mode === "per_customer" && g.rows.length > 1;
  const mixed = merged && new Set(g.rows.map((r) => !!r.odoo_synced)).size > 1;
  return (
    <div className="bulk-group" data-testid={`bulk-group-${g.cid}`}>
      <div className="bulk-group-head">
        <div><strong>{g.outlet}</strong><small className="fin-muted">{g.rows.map((r) => r.so_number).join(", ")}</small></div>
        <div className="bulk-group-amt"><b>{rupiah(total)}</b>
          <span className={`bill-chip ${merged ? "merged" : "partial"}`}>{merged ? "1 invoice gabungan" : `${g.rows.length} invoice`}</span></div>
      </div>
      <div className="bulk-group-term">
        <label className="rj-field">AR Term invoice
          <TermSelect terms={terms} value={termId} onChange={setTerm} placeholder={g.head.customer_in_app ? "Tunai / Immediate" : "Ikuti termin di Odoo"} testId={`bulk-term-${g.cid}`} />
        </label>
        <div className="fin-kv"><span>Perkiraan jatuh tempo</span><b>{!g.head.customer_in_app && !termId ? "Ditentukan Odoo" : fmtDate(due)}</b></div>
      </div>
      <TermHint row={g.head} termId={termId} terms={terms} testId={`bulk-term-hint-${g.cid}`} />
      {mixed && <small className="term-hint warn"><AlertTriangle size={12} /> Sebagian order belum ada di Odoo — invoice gabungan untuk customer ini akan gagal. Pilih mode 1 invoice per order.</small>}
    </div>
  );
}

function Results({ res }) {
  return (
    <div className="bulk-results" data-testid="bulk-results">
      <p><b>{res.meta.created}</b> invoice dibuat{res.meta.failed ? `, ${res.meta.failed} gagal` : ""}.</p>
      {res.data.map((r, i) => (
        <div key={i} className={`bulk-result ${r.ok ? "ok" : "bad"}`} data-testid={`bulk-result-${i}`}>
          {r.ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
          <div><strong>{r.ok ? r.number : r.outlet}</strong>
            <small>{r.so_numbers.join(", ")}{r.ok ? ` · ${rupiah(r.amount_total)}${r.odoo_synced ? " · Odoo" : ""}` : ` · ${r.error}`}</small>
            {r.term_warning && <small className="term-hint warn">{r.term_warning}</small>}</div>
        </div>
      ))}
    </div>
  );
}

export function BulkBillDialog({ rows, onClose, onSaved }) {
  const terms = usePaymentTerms();
  const groups = useMemo(() => groupByCustomer(rows), [rows]);
  const [mode, setMode] = useState("per_order");
  const [invDate, setInvDate] = useState(todayStr());
  const [termSel, setTermSel] = useState(() => Object.fromEntries(groups.map((g) => [g.cid, g.head.ar_term_odoo_id || null])));
  const [saving, setSaving] = useState(false);
  const [res, setRes] = useState(null);
  const count = mode === "per_order" ? rows.length : groups.length;

  const submit = async () => {
    setSaving(true);
    try {
      const overrides = Object.fromEntries(Object.entries(termSel).filter(([, v]) => v));
      const r = await api.post("/finance/invoices/bulk", { sales_ids: rows.map((x) => x.sales_id), mode, invoice_date: invDate, term_overrides: overrides });
      setRes(r.data);
      r.data.meta.failed ? toast.warning(`${r.data.meta.created} invoice dibuat, ${r.data.meta.failed} gagal`) : toast.success(`${r.data.meta.created} invoice dibuat`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const footer = res ? <button className="rj-btn rj-btn-primary" onClick={onSaved} data-testid="bulk-done-btn">Selesai</button> : <>
    <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="bulk-cancel-btn">Batal</button>
    <button className="rj-btn rj-btn-primary" onClick={submit} disabled={saving} data-testid="bulk-submit-btn">
      {saving ? <Loader2 size={16} className="spin" /> : <><Receipt size={16} /> Buat {count} Invoice</>}
    </button></>;

  return (
    <Modal open onClose={res ? onSaved : onClose} className="fin-dialog bulk-dialog" title={`Tagihkan ${rows.length} order terpilih`}
      subtitle={`${groups.length} customer`} testId="bulk-bill-dialog" footer={footer}>
      {res ? <Results res={res} /> : <>
        <div className="bulk-mode" data-testid="bulk-mode">
          <button className={mode === "per_order" ? "active" : ""} onClick={() => setMode("per_order")} data-testid="bulk-mode-per-order">
            <Files size={18} /><div><strong>1 invoice per order</strong><small>{rows.length} invoice terpisah</small></div></button>
          <button className={mode === "per_customer" ? "active" : ""} onClick={() => setMode("per_customer")} data-testid="bulk-mode-per-customer">
            <Layers size={18} /><div><strong>Gabung per customer</strong><small>{groups.length} invoice · order customer yang sama jadi 1</small></div></button>
        </div>
        <label className="rj-field bulk-date">Tanggal Penagihan
          <input type="date" className="rj-input" value={invDate} onChange={(e) => setInvDate(e.target.value || todayStr())} data-testid="bulk-invoice-date" />
        </label>
        <div className="bulk-groups">
          {groups.map((g) => <GroupCard key={g.cid} g={g} mode={mode} terms={terms} invDate={invDate}
            termId={termSel[g.cid]} setTerm={(v) => setTermSel((s) => ({ ...s, [g.cid]: v }))} />)}
        </div>
        <div className="activity-totals"><div className="grand"><span>Estimasi total tagihan</span>
          <strong data-testid="bulk-total">{rupiah(rows.reduce((s, r) => s + r.estimate.amount, 0))}</strong></div></div>
      </>}
    </Modal>
  );
}
